import { useRef, useState, type PointerEvent, type CSSProperties } from 'react';
import { swipeDirection } from './mobileLayout';

export function useCardSwipe(
  activeId: string | null,
  ids: string[],
  onSelect: (id: string) => void,
) {
  const gesture = useRef<{
    pointer: number;
    x: number;
    y: number;
    time: number;
    id: string;
  } | null>(null);
  const [drag, setDrag] = useState(0);
  const [arrival, setArrival] = useState<{
    id: string;
    direction: number;
  } | null>(null);
  const suppressClick = useRef(false);
  const reset = () => {
    gesture.current = null;
    setDrag(0);
  };
  return {
    arrival: arrival?.id === activeId ? arrival.direction : 0,
    handlers: {
      style: { '--swipe-drag': drag + 'px' } as CSSProperties,
      'data-dragging': drag !== 0,
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        suppressClick.current = false;
        if (
          event.pointerType === 'mouse' ||
          !event.isPrimary ||
          !activeId ||
          ids.length < 2 ||
          !window.matchMedia('(max-width: 760px)').matches ||
          (event.target as Element).closest(
            'button, input, a, select, textarea, [role="button"]',
          )
        )
          return;
        gesture.current = {
          pointer: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          time: event.timeStamp,
          id: activeId,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
        const start = gesture.current;
        if (!start || start.pointer !== event.pointerId) return;
        if (start.id !== activeId) {
          reset();
          return;
        }
        const dx = event.clientX - start.x,
          dy = event.clientY - start.y;
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
          reset();
          return;
        }
        if (Math.abs(dx) > 8) {
          suppressClick.current = true;
          setDrag(Math.max(-64, Math.min(64, dx * 0.28)));
        }
      },
      onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
        const start = gesture.current;
        if (!start || start.pointer !== event.pointerId) return;
        const direction = swipeDirection(
          event.clientX - start.x,
          event.clientY - start.y,
          event.timeStamp - start.time,
        );
        const index = ids.indexOf(start.id);
        const next = index >= 0 ? ids[index + direction] : undefined;
        if (direction && next && start.id === activeId) {
          setArrival({ id: next, direction });
          onSelect(next);
        }
        reset();
      },
      onPointerCancel: reset,
      onLostPointerCapture: reset,
      onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
        if (suppressClick.current) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick.current = false;
        }
      },
    },
  };
}
